import React = require("react");
import {RestDataLoader} from "../../../dashboard/widgets/unilever/RestDataLoader";
import {UnitOfMeasureConverter} from "../../../components/Utils/UnitOfMeasureConverter";
import {EquipmentProductCategorySettingsContent} from "./EquipmentProductCategoryContent";
import "./EquipmentProductCategorySettings.scss";
import {SettingSection} from "./SettingSection";
import {defaultTheme} from "@vebox/etui-themes";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../../../index";

enum EquipmentProductCategorySettingsStatus {
    /** The tab is fetching the configuration from the server. */
    Initializing,
    /** The tab is ready to display the data. */
    Ready,
}

declare var RestApi: any;

export interface ProductCategory {
    guid: string;
    name: string;
    maxTemperature: number;
    minTemperature: number;
}

export interface EquipmentProductCategoryConfiguration {
    title: string;
    inEditingText: string;
    configuredTitleValue: string;
    notConfiguredTitleValue: string;
    editButtonText: string;
    locale: string;

    selectProductCategoryText: string;
    insertTemperatureLimitsText: string;
    minTemperatureText: string;
    maxTemperatureText: string;

    saveButtonText: string;
    cancelButtonText: string;
    deleteConfButtonText: string;
    noProductText: string;
    recommendedThresholds: string;
}

export interface DiagnosticType {
    id: number;
    guid: string;
    name: string;
}

export interface ComponentProductCategoryConf {
    diagnosticType: DiagnosticType;
    productCategory: ProductCategory;
    minTemperature: number;
    maxTemperature: number;
}

interface EquipmentProductCategorySettingsProps {
    onOpen: () => any;
    closeOthers: () => any;
    configuration: EquipmentProductCategoryConfiguration;
    productCategories: ProductCategory[];
    componentProductCategoryConfig: ComponentProductCategoryConf;
    equipmentId: number;
    allowEditing: boolean;
    equipmentGuid: string;
}

interface EquipmentProductCategorySettingsState {
    status: EquipmentProductCategorySettingsStatus;
    currentComponentProductCategory: ComponentProductCategoryConf;
    selectedProductCategory: ProductCategory;
    insertedMinTemp: number;
    insertedMaxTemp: number;
    closed: boolean;
    error: string;
}

export class EquipmentProductCategorySettings
    extends React.Component<EquipmentProductCategorySettingsProps, EquipmentProductCategorySettingsState> {
    private static logEnabled: boolean = false;

    private static log(message: any): void {
        if (EquipmentProductCategorySettings.logEnabled) {
            console.log("EquipmentProductCategorySettings: " + message);
        }
    }

    private static warn(message: any): void {
        if (EquipmentProductCategorySettings.logEnabled) {
            console.warn("EquipmentProductCategorySettings: " + message);
        }
    }

    private static areCategoriesDifferent(c1: ProductCategory, c2: ProductCategory) {
        return (c1 == null && c2 != null)
            || (c1 != null && c2 == null)
            || (c1 != null && c2 != null && c1.guid !== c2.guid);
    }

    private static areTemperaturesDifferent(t1: number, t2: number) {
        return t1 != null && t2 != null && t1 !== t2;
    }

    constructor(props: EquipmentProductCategorySettingsProps) {
        super(props);
        this.onCategoryChange = this.onCategoryChange.bind(this);
        this.onMinTemperatureChange = this.onMinTemperatureChange.bind(this);
        this.onMaxTemperatureChange = this.onMaxTemperatureChange.bind(this);
        this.onSaveError = this.onSaveError.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.save = this.save.bind(this);
        this.close = this.close.bind(this);
        this.deleteConf = this.deleteConf.bind(this);
        let currentMinTemp: number;
        let currentMaxTemp: number;
        const selectedProductCategory = this.props.componentProductCategoryConfig.productCategory;
        currentMaxTemp = this.props.componentProductCategoryConfig.maxTemperature;
        currentMinTemp = this.props.componentProductCategoryConfig.minTemperature;
        this.state = {
            status: EquipmentProductCategorySettingsStatus.Ready,
            currentComponentProductCategory: this.props.componentProductCategoryConfig,
            selectedProductCategory,
            insertedMinTemp: currentMinTemp,
            insertedMaxTemp: currentMaxTemp,
            closed: true,
            error: null,
        };
    }

    public close(): void {
        if (this.state.closed) {
            return;
        }
        const category = this.state.currentComponentProductCategory != null ?
            this.state.currentComponentProductCategory.productCategory :
            null;
        if (category == null) {
            this.setState(
                {
                    closed: true,
                    selectedProductCategory: null,
                    insertedMaxTemp: null,
                    insertedMinTemp: null,
                    error: null,
                },
            );
        } else {
            this.setState(
                {
                    closed: true,
                    selectedProductCategory: category,
                    insertedMaxTemp: this.state.currentComponentProductCategory.maxTemperature,
                    insertedMinTemp: this.state.currentComponentProductCategory.minTemperature,
                    error: null,
                },
            );
        }

    }

    public render(): JSX.Element {
        if (this.state.status === EquipmentProductCategorySettingsStatus.Initializing) {
            return null;
        }
        if (this.props.productCategories == null || this.props.productCategories.length === 0) {
            EquipmentProductCategorySettings.warn("No product categories found! Check the mapping");
            return null;
        }
        const content: JSX.Element = (
            <EquipmentProductCategorySettingsContent
                configuration={
                    {
                        selectProductCategoryText: this.props.configuration.selectProductCategoryText,
                        insertTemperatureLimitsText: this.props.configuration.insertTemperatureLimitsText,
                        minTemperatureText: this.props.configuration.minTemperatureText,
                        maxTemperatureText: this.props.configuration.maxTemperatureText,
                        locale: this.props.configuration.locale,
                        notSetTypeOptionText: this.props.configuration.noProductText,
                        recommendedThresholds: this.props.configuration.recommendedThresholds,
                    }
                }
                selectedProductCategory={this.state.selectedProductCategory}
                minTemperature={this.state.insertedMinTemp}
                maxTemperature={this.state.insertedMaxTemp}
                productCategories={this.props.productCategories}
                onCategoryChange={this.onCategoryChange}
                onMinTemperatureChange={this.onMinTemperatureChange}
                onMaxTemperatureChange={this.onMaxTemperatureChange}
                unitOfMeasure={UnitOfMeasureConverter.getTemperatureUnitOfMeasure(RestApi.getCurrentLang())}
                error={this.state.error}
            />
        );
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <SettingSection
                    title={"HACCP " + this.props.componentProductCategoryConfig.diagnosticType.name}
                    content={null}
                    editingContent={content}
                    onToggle={this.onToggle}
                    onSave={this.save}
                    onCancel={this.close}
                    onDeleteConf={this.deleteConf}
                    configuration={this.props.configuration}
                    contentTitle={(this.state.currentComponentProductCategory.productCategory != null)
                        ? this.formatTitle(this.props.configuration.configuredTitleValue)
                        : this.props.configuration.notConfiguredTitleValue}
                    isInEditing={!this.state.closed}
                    showSaveButton={this.showSaveButton()}
                    allowEditing={this.props.allowEditing}
                />
            </ThemeProvider>
        );
    }

    private formatTitle(notFormatted: string): string {
        const lang = RestApi.getCurrentLang();
        return notFormatted
            .replace("{0}", this.state.currentComponentProductCategory.productCategory.name)
            .replace("{1}", this.state.currentComponentProductCategory.minTemperature.toString())
            .replace("{2}", this.state.currentComponentProductCategory.maxTemperature.toString())
            .replace("{3}", UnitOfMeasureConverter.getTemperatureUnitOfMeasure(lang))
            .replace("{4}", UnitOfMeasureConverter.getTemperatureUnitOfMeasure(lang));
    }

    private onToggle(): void {
        if (this.state.closed) {
            this.setState({closed: false});
            this.props.closeOthers();
            this.props.onOpen();
        } else {
            this.close();
        }
    }

    private onSaveError(error: string) {
        this.setState({error});
    }

    private onCategoryChange(newCategory: ProductCategory): void {
        if (newCategory == null) {
            this.setState(
                {
                    selectedProductCategory: null,
                    insertedMaxTemp: null,
                    insertedMinTemp: null,
                    error: null,
                },
            );
        } else {
            this.setState(
                {
                    selectedProductCategory: newCategory,
                    insertedMaxTemp: newCategory.maxTemperature,
                    insertedMinTemp: newCategory.minTemperature,
                    error: null,
                },
            );
        }
    }

    private onMinTemperatureChange(newTemperature: number): void {
        this.setState({
            insertedMinTemp: newTemperature,
            error: null,
        });
    }

    private onMaxTemperatureChange(newTemperature: number): void {
        this.setState({
            insertedMaxTemp: newTemperature,
            error: null,
        });
    }

    private save(): void {
        const category = this.state.selectedProductCategory;
        const lang = RestApi.getCurrentLang();
        const minTemp = this.state.insertedMinTemp;
        const maxTemp = this.state.insertedMaxTemp;
        const url = "equipment/" + this.props.equipmentGuid
            + "/haccp/" + this.props.componentProductCategoryConfig.diagnosticType.guid;
        EquipmentProductCategorySettings.log("Saving data.....");
        if (category != null) {
            RestDataLoader.putJSON(
                url,
                {
                    productCategoryGuid: category.guid,
                    minTemperature: UnitOfMeasureConverter.convertTemperature(minTemp, "celsius", lang),
                    maxTemperature: UnitOfMeasureConverter.convertTemperature(maxTemp, "celsius", lang),
                },
                (data: any) => {
                    this.close();
                    this.setState({
                        currentComponentProductCategory: {
                            diagnosticType: this.props.componentProductCategoryConfig.diagnosticType,
                            productCategory: category,
                            minTemperature: minTemp,
                            maxTemperature: maxTemp,
                        },
                        insertedMaxTemp: maxTemp,
                        insertedMinTemp: minTemp,
                        selectedProductCategory: category,
                    });
                },
                (error?: any) => {
                    if (error && error.responseText !== "") {
                        this.onSaveError(error.responseText);
                    } else {
                        this.onSaveError("Unexpected error, please try again later");
                    }
                },
            );
        } else {
            this.deleteConf();
        }
    }

    private deleteConf(): void {
        const url = "equipment/" + this.props.equipmentGuid
            + "/haccp/" + this.props.componentProductCategoryConfig.diagnosticType.guid;
        RestDataLoader.deleteJSON(
            url,
            {},
            () => {
                this.close();
                this.setState({
                    currentComponentProductCategory: {
                        diagnosticType: this.props.componentProductCategoryConfig.diagnosticType,
                        productCategory: null,
                        minTemperature: null,
                        maxTemperature: null,
                    },
                    insertedMaxTemp: null,
                    insertedMinTemp: null,
                    selectedProductCategory: null,
                });
            },
            (error?: any) => {
                if (error && error.responseText !== "") {
                    this.onSaveError(error.responseText);
                } else {
                    this.onSaveError("Unexpected error, please try again later");
                }
            },
        );
    }

    private showSaveButton(): boolean {
        const currentProductCategory = this.state.currentComponentProductCategory != null ?
            this.state.currentComponentProductCategory.productCategory :
            null;
        const currentMinTemp = currentProductCategory != null
            ? this.state.currentComponentProductCategory.minTemperature
            : null;
        const currentMaxTemp = currentProductCategory != null
            ? this.state.currentComponentProductCategory.maxTemperature
            : null;
        return EquipmentProductCategorySettings.areCategoriesDifferent(
                this.state.selectedProductCategory,
                currentProductCategory,
            )
            || EquipmentProductCategorySettings.areTemperaturesDifferent(this.state.insertedMinTemp, currentMinTemp)
            || EquipmentProductCategorySettings.areTemperaturesDifferent(this.state.insertedMaxTemp, currentMaxTemp);
    }
}
